import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Upload } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { resolveImg } from "@/contexts/SettingsContext";
import { useSettings } from "@/contexts/SettingsContext";
import { useLang } from "@/contexts/LanguageContext";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABEL = {
  de: { mon: "Montag", tue: "Dienstag", wed: "Mittwoch", thu: "Donnerstag", fri: "Freitag", sat: "Samstag", sun: "Sonntag" },
  en: { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" },
  tr: { mon: "Pazartesi", tue: "Salı", wed: "Çarşamba", thu: "Perşembe", fri: "Cuma", sat: "Cumartesi", sun: "Pazar" },
};
const IMG_KEYS = ["hero", "store", "cat_brautmode", "cat_abendkleider", "cat_hennakleidung", "cat_verlobungskleidung", "cat_herrenanzuege"];

export default function SettingsPanel() {
  const { t, lang } = useLang();
  const { reload } = useSettings();
  const [hours, setHours] = useState({});
  const [interval, setIntervalVal] = useState(60);
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setHours(r.data.opening_hours || {});
      setIntervalVal(r.data.slot_interval || 60);
      setImages(r.data.images || {});
    }).finally(() => setLoading(false));
  }, []);

  const setDay = (d, field, val) => setHours((h) => ({ ...h, [d]: { ...h[d], [field]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { opening_hours: hours, slot_interval: parseInt(interval) || 60, images });
      toast.success(t("save"));
      reload();
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const upload = async (key, file) => {
    if (!file) return;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...images, [key]: data.url };
      setImages(next);
      await api.put("/admin/settings", { images: next });
      reload();
      toast.success(t("upload") + " ✓");
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
    finally { setUploadingKey(null); }
  };

  if (loading) return <div className="py-16 text-center"><Loader2 className="animate-spin text-[var(--vl-gold)] mx-auto" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="settings-panel">
      {/* opening hours */}
      <div className="vl-card rounded-xl p-6">
        <h3 className="font-serif-display text-xl text-[var(--vl-ivory)] mb-5">{t("opening_hours_title")}</h3>
        <div className="space-y-2.5">
          {DAYS.map((d) => {
            const h = hours[d] || { open: "10:00", close: "19:00", closed: false };
            return (
              <div key={d} className="flex items-center gap-2 flex-wrap" data-testid={`hours-row-${d}`}>
                <span className="w-24 text-sm text-[var(--vl-ivory)]">{DAY_LABEL[lang][d]}</span>
                <label className="flex items-center gap-1.5 text-xs text-[var(--vl-stone)]">
                  <input type="checkbox" checked={!h.closed} onChange={(e) => setDay(d, "closed", !e.target.checked)} data-testid={`hours-open-${d}`} />
                  {h.closed ? t("closed") : ""}
                </label>
                {!h.closed && (
                  <>
                    <input type="time" value={h.open || "10:00"} onChange={(e) => setDay(d, "open", e.target.value)} className="vl-input rounded px-2 py-1.5 text-sm" data-testid={`hours-from-${d}`} />
                    <span className="text-[var(--vl-stone)]">–</span>
                    <input type="time" value={h.close || "19:00"} onChange={(e) => setDay(d, "close", e.target.value)} className="vl-input rounded px-2 py-1.5 text-sm" data-testid={`hours-to-${d}`} />
                  </>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-5">
          <label className="block text-xs tracking-widest uppercase text-[var(--vl-stone)] mb-2">{t("slot_interval_label")}</label>
          <input type="number" min="15" step="15" value={interval} onChange={(e) => setIntervalVal(e.target.value)} className="vl-input rounded-lg px-4 py-2.5 w-32" data-testid="slot-interval-input" />
        </div>
        <button onClick={save} disabled={saving} className="vl-btn-gold rounded-full px-6 py-2.5 text-sm font-medium flex items-center gap-2 mt-6" data-testid="save-settings-btn">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {t("save")}
        </button>
      </div>

      {/* images */}
      <div className="vl-card rounded-xl p-6">
        <h3 className="font-serif-display text-xl text-[var(--vl-ivory)] mb-5">{t("images_title")}</h3>
        <div className="space-y-3">
          {IMG_KEYS.map((key) => (
            <div key={key} className="flex items-center gap-3" data-testid={`image-row-${key}`}>
              <img src={resolveImg(images[key])} alt="" className="w-16 h-16 rounded-lg object-cover border border-[var(--vl-border)]" />
              <span className="flex-1 text-sm text-[var(--vl-ivory)]">{key === "hero" ? t("img_hero") : key === "store" ? t("img_store") : t(key)}</span>
              <label className="vl-btn-outline rounded-full px-4 py-2 text-xs flex items-center gap-1.5 cursor-pointer">
                {uploadingKey === key ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {t("upload")}
                <input type="file" accept="image/*" className="hidden" data-testid={`upload-${key}`}
                  onChange={(e) => upload(key, e.target.files?.[0])} />
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
